import BusinessIcon from '@mui/icons-material/Business';
import DomainIcon from '@mui/icons-material/Domain';
import GroupsIcon from '@mui/icons-material/Groups';
import SecurityIcon from '@mui/icons-material/Security';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';

import type { NavItem } from '@/components/layout/Sidebar';
import type { ErpModule } from '@/api/platform.api';
import { LEGACY_GLOBAL_WORK_ROUTES } from '@/constants/globalWorkspaceRoutes';
import { moduleWorkRoutes } from '@/config/moduleWorkPaths';
import { FEATURE } from '@/core/features/featureCodes';

/**
 * URL slugs for launcher modules — keep in sync with {@link ModuleEntry} in moduleEntries.ts.
 * Defined here (not in moduleEntries) to avoid a circular import at startup.
 */
export const ERP_MODULE_SLUGS: Partial<Record<ErpModule, string>> = {
  POS: 'pos',
  CRM: 'crm',
  HR: 'hr',
  DESK: 'desk',
  FINANCE: 'finance',
  PROJECTS: 'projects',
  PROCUREMENT: 'procurement',
  WAREHOUSE: 'warehouse',
  SOCIAL: 'social',
  LEAD_GEN: 'lead-gen',
  MARKETING: 'marketing',
};

export const LAUNCHER_ERP_MODULES = Object.keys(ERP_MODULE_SLUGS) as ErpModule[];

export type ModuleAdminPage =
  | 'administration'
  | 'profile'
  | 'organization'
  | 'companies'
  | 'users'
  | 'roles'
  | 'settings'
  | 'subscription';

/** Legacy org-wide admin paths (redirect to module-scoped routes). */
export const LEGACY_ORG_ADMIN_ROUTE_PREFIXES = [
  '/org/administration',
  '/org/profile',
  '/org/employees',
  '/org/roles',
  '/org/workspace',
  '/org/subscription',
  '/org/settings',
] as const;

export const MODULE_DEFAULT_API_PREFIXES = [
  '/v1/employees',
  '/v1/roles',
  '/v1/permissions',
  '/v1/companies',
] as const;

export const MODULE_DEFAULT_PERMISSION_MODULES = [
  'EMPLOYEE',
  'ROLE',
  'ORGANIZATION',
  'DASHBOARD',
  'REPORT',
] as const;

/** Subscription upsell keys (may differ from ERP module slugs). */
export const LOCKED_MODULE_KEYS = [
  'pos',
  'hr',
  'payroll',
  'finance',
  'crm',
  'delivery',
  'ecommerce',
  'analytics',
  'loyalty',
  'social',
  'desk',
  'projects',
  'procurement',
  'inventory',
  'warehouse',
  'marketing',
  'lead-gen',
] as const;

/** @deprecated Use moduleSettingsLink(module) */
export const MODULE_SETTINGS_PATH = '/pos/profile?section=preferences';
/** @deprecated Use moduleSubscriptionLink(module) */
export const MODULE_SUBSCRIPTION_PATH = '/pos/subscription';

export function moduleSlug(erpModule: ErpModule): string {
  const slug = ERP_MODULE_SLUGS[erpModule];
  if (slug) return slug;
  return erpModule.toLowerCase().replace(/_/g, '-');
}

/** Platform login users segment — HR uses `users` because `/hr/employees` is HR employee records. */
export function moduleUsersSegment(erpModule: ErpModule): 'employees' | 'users' {
  return erpModule === 'HR' ? 'users' : 'employees';
}

export function moduleAdminPath(erpModule: ErpModule, page: ModuleAdminPage): string {
  const slug = moduleSlug(erpModule);
  if (page === 'users') {
    return `/${slug}/${moduleUsersSegment(erpModule)}`;
  }
  return `/${slug}/${page}`;
}

export function moduleLockedPath(erpModule: ErpModule): string {
  return `/${moduleSlug(erpModule)}/locked`;
}

export function lockedModulePath(moduleKey: string): string {
  return `/${moduleKey}/locked`;
}

/** Module-scoped administration routes for a single ERP module. */
/** Personal account + legacy module settings — header menu only, not Administration nav or roles matrix. */
export function isExcludedFromAdministrationNav(pathname: string): boolean {
  const path = pathname.split('?')[0];
  if (path === '/master/profile') return true;
  const parts = path.split('/').filter(Boolean);
  if (parts.length !== 2) return false;
  const [slug, page] = parts;
  if (!Object.values(ERP_MODULE_SLUGS).includes(slug)) return false;
  return page === 'settings' || page === 'profile';
}

export function moduleAdminRoutes(erpModule: ErpModule): string[] {
  return [
    moduleAdminPath(erpModule, 'administration'),
    moduleAdminPath(erpModule, 'profile'),
    moduleAdminPath(erpModule, 'organization'),
    moduleAdminPath(erpModule, 'companies'),
    moduleAdminPath(erpModule, 'users'),
    moduleAdminPath(erpModule, 'roles'),
    moduleAdminPath(erpModule, 'settings'),
    moduleAdminPath(erpModule, 'subscription'),
    moduleLockedPath(erpModule),
  ];
}

export function moduleDefaultRoutes(erpModule: ErpModule, ...extras: string[]): string[] {
  return [
    ...moduleAdminRoutes(erpModule),
    '/settings',
    ...LEGACY_ORG_ADMIN_ROUTE_PREFIXES,
    ...moduleWorkRoutes(erpModule),
    ...LEGACY_GLOBAL_WORK_ROUTES,
    '/dashboard/home',
    '/onboarding',
    ...extras,
  ];
}

export function moduleDefaultApis(...extras: string[]): string[] {
  return [...MODULE_DEFAULT_API_PREFIXES, ...extras];
}

export function moduleDefaultPermissionModules(...extras: string[]): string[] {
  return [...new Set([...MODULE_DEFAULT_PERMISSION_MODULES, ...extras])];
}

/** Legacy `/org/*` and module-scoped admin URLs — cross-cutting workspace administration. */
export function isPlatformAdminRoute(pathname: string): boolean {
  if (pathname === '/org' || pathname === '/onboarding' || pathname.startsWith('/onboarding/')) {
    return true;
  }
  if (pathname === '/settings' || pathname.startsWith('/settings/')) {
    return true;
  }
  if (resolveModuleFromAdminPath(pathname) != null) {
    return true;
  }
  return LEGACY_ORG_ADMIN_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function resolveModuleFromAdminPath(pathname: string): ErpModule | null {
  for (const erpModule of LAUNCHER_ERP_MODULES) {
    const routes = moduleAdminRoutes(erpModule);
    if (routes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
      return erpModule;
    }
  }
  return null;
}

export function resolveModuleAdminRoutePermission(
  pathname: string,
): string | string[] | null | undefined {
  const module = resolveModuleFromAdminPath(pathname);
  if (!module) return undefined;
  const slug = moduleSlug(module);
  const usersPath = `/${slug}/${moduleUsersSegment(module)}`;
  if (pathname === usersPath || pathname.startsWith(`${usersPath}/`)) {
    return ['EMPLOYEE_VIEW', 'ORG_MANAGE'];
  }
  if (pathname === `/${slug}/roles` || pathname.startsWith(`/${slug}/roles/`)) {
    return 'ROLE_VIEW';
  }
  if (pathname === `/${slug}/profile` || pathname.startsWith(`/${slug}/profile/`)) {
    return null;
  }
  if (pathname === `/${slug}/organization` || pathname.startsWith(`/${slug}/organization/`)) {
    return ['ORG_VIEW', 'ORG_MANAGE'];
  }
  if (pathname === `/${slug}/companies` || pathname.startsWith(`/${slug}/companies/`)) {
    return ['COMPANY_VIEW', 'ORG_MANAGE'];
  }
  if (pathname === `/${slug}/administration` || pathname.startsWith(`/${slug}/administration/`)) {
    return ['ORG_MANAGE', 'ORG_VIEW'];
  }
  if (pathname === `/${slug}/subscription` || pathname.startsWith(`/${slug}/subscription/`)) {
    return ['SUBSCRIPTION_MANAGE', 'ORG_MANAGE'];
  }
  if (pathname === `/${slug}/settings` || pathname.startsWith(`/${slug}/settings/`)) {
    return null;
  }
  return undefined;
}

export function resolveModuleAdminRouteFeature(pathname: string): string | null | undefined {
  const module = resolveModuleFromAdminPath(pathname);
  if (!module) return undefined;
  const slug = moduleSlug(module);
  const usersPath = `/${slug}/${moduleUsersSegment(module)}`;
  if (pathname === usersPath || pathname.startsWith(`${usersPath}/`)) {
    return FEATURE.EMPLOYEE_BASIC;
  }
  if (pathname === `/${slug}/roles` || pathname.startsWith(`/${slug}/roles/`)) {
    return FEATURE.EMPLOYEE_BASIC;
  }
  if (
    pathname === `/${slug}/administration` ||
    pathname === `/${slug}/settings` ||
    pathname === `/${slug}/subscription` ||
    pathname === `/${slug}/profile` ||
    pathname === `/${slug}/organization`
  ) {
    return null;
  }
  return undefined;
}

/** Administration sidebar items for a module workspace. */
export function getModuleAdminNavItems(module: ErpModule): NavItem[] {
  return [
    { to: moduleAdminPath(module, 'organization'), label: 'Organization', icon: BusinessIcon },
    { to: moduleAdminPath(module, 'companies'), label: 'Companies', icon: DomainIcon },
    {
      to: moduleAdminPath(module, 'users'),
      label: 'Users',
      icon: GroupsIcon,
      feature: FEATURE.EMPLOYEE_BASIC,
    },
    {
      to: moduleAdminPath(module, 'roles'),
      label: 'Roles & screen access',
      icon: SecurityIcon,
      feature: FEATURE.EMPLOYEE_BASIC,
    },
    { to: moduleSubscriptionLink(module), label: 'Subscription', icon: SubscriptionsIcon },
  ].filter((item) => !isExcludedFromAdministrationNav(item.to));
}

/** @deprecated Use getModuleAdminNavItems(module) */
export function moduleAdminNavItems(module: ErpModule = 'POS'): NavItem[] {
  return getModuleAdminNavItems(module).filter(
    (item) => item.to !== moduleSubscriptionLink(module),
  );
}

/** @deprecated Settings moved to My profile — kept for legacy redirects. */
export function moduleSettingsLink(module: ErpModule): string {
  return `${moduleAdminPath(module, 'profile')}?section=preferences`;
}

export function moduleSubscriptionLink(module: ErpModule): string {
  return moduleAdminPath(module, 'subscription');
}

export function moduleWorkspaceNavItems(module: ErpModule): NavItem[] {
  return [
    { to: moduleSubscriptionLink(module), label: 'Subscription', icon: SubscriptionsIcon },
  ];
}

export function dedupeNavItems(items: NavItem[]): NavItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.to.split('?')[0];
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
